import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Building2, 
  Users, 
  UserPlus, 
  Settings, 
  Crown,
  Shield,
  Palette,
  Mail,
  ArrowLeft,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const OwnerDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [department, setDepartment] = useState(null);
  const [stats, setStats] = useState({});
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Admin creation form
  const [newAdminForm, setNewAdminForm] = useState({
    full_name: '',
    email: '',
    password: ''
  });
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  useEffect(() => {
    fetchOwnerData();
  }, []);

  const fetchOwnerData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch department info
      const deptResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/departments/current`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDepartment(deptResponse.data);
      
      // Fetch basic stats
      const statsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(statsResponse.data);
      
      // Fetch admin users
      const adminsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users?role=admin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAdmins(Array.isArray(adminsResponse.data) ? adminsResponse.data.filter(u => u.role === 'admin') : []);
      
    } catch (error) {
      console.error('Error fetching owner data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreatingAdmin(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/create-staff`,
        { ...newAdminForm, role: 'admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Admin created successfully');
      setNewAdminForm({ full_name: '', email: '', password: '' });
      fetchOwnerData(); // Refresh data
      
    } catch (error) {
      console.error('Error creating admin:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create admin';
      toast.error(errorMessage);
    } finally {
      setCreatingAdmin(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with mobile-responsive layout */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 w-fit"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Owner Dashboard</h1>
            </div>
            <p className="text-slate-600 mt-1 text-sm sm:text-base">Manage your department's administration and settings</p>
          </div>
        </div>
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
          <Crown className="w-4 h-4 mr-1" />
          Department Owner
        </Badge>
      </div>

      {/* Department Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Department Info
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-gray-600">Name</p>
              <p className="font-medium">{department?.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Subdomain</p>
              <p className="font-medium">{department?.subdomain}.reqtrak.com</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <Badge className="bg-green-100 text-green-800">
                {department?.subscription_status || 'Active'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              Department Stats
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-slate-800">{stats.total_requests || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-slate-800">{stats.total_users || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Admins</p>
              <p className="text-2xl font-bold text-slate-800">{admins.length}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-600" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              onClick={() => navigate('/dashboard', { state: { tab: 'settings' } })}
              className="w-full justify-start"
              variant="outline"
            >
              <Palette className="w-4 h-4 mr-2" />
              Department Settings
            </Button>
            <Button 
              onClick={() => window.open('mailto:support@reqtrak.com')}
              className="w-full justify-start"
              variant="outline"
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Support
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Admin Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Create New Admin */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Create Department Admin
            </CardTitle>
            <CardDescription>
              Admins can manage daily operations, users, and requests
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-name">Full Name</Label>
                <Input
                  id="admin-name"
                  value={newAdminForm.full_name}
                  onChange={(e) => setNewAdminForm(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="John Smith"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email Address</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={newAdminForm.email}
                  onChange={(e) => setNewAdminForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="admin@department.gov"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={newAdminForm.password}
                  onChange={(e) => setNewAdminForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Secure password"
                  required
                />
              </div>
              
              <Button type="submit" disabled={creatingAdmin} className="w-full">
                {creatingAdmin ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating Admin...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create Admin
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Current Admins */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              Department Admins
            </CardTitle>
            <CardDescription>
              Current administrators for your department
            </CardDescription>
          </CardHeader>
          <CardContent>
            {admins.length === 0 ? (
              <div className="text-center py-4">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No admins created yet</p>
                <p className="text-sm text-gray-400">Create your first admin to delegate management tasks</p>
              </div>
            ) : (
              <div className="space-y-3">
                {admins.map((admin) => (
                  <div key={admin.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{admin.full_name}</p>
                      <p className="text-sm text-gray-600">{admin.email}</p>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700">
                      <Shield className="w-3 h-3 mr-1" />
                      Admin
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Owner Information */}
      <Alert className="bg-amber-50 border-amber-200">
        <Crown className="h-4 w-4 text-amber-600" />
        <AlertDescription>
          <strong className="text-amber-800">Department Owner Privileges:</strong> 
          <span className="text-amber-700"> As the owner, you have full control over department branding, settings, and admin management. 
          You can access Department Settings to customize your portal's appearance and contact information.</span>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default OwnerDashboard;